import React, { useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import Header from './components/Header';
import Filters from './components/Filters';
import Row from './components/Row';
import TableHeaderSection from './components/TableHeaderSection';
import TableBox from '../../../components/TableBox';
import { HeadCellType, TableContextProvider } from '../../../interfaces/common';
import { ListFilters } from '../../../interfaces/reservation.interface';
import { buildHeadCells, getStartEndOfMonth } from '../../../utils/methods';
import { GENERIC_LOOKUP_KEYS, VALIDATION_FIELD_MAPPING, HEAD_CELL_SORT_MAPPING } from '../../../utils/appConstant';
import useSorting from '../../../hooks/useSorting';
import usePagination from '../../../hooks/usePagination';
import useUrlParams from '../../../hooks/useUrlParams';
import useFetch from '../../../hooks/useFetch';
import ReservationService from '../../../services/reservation.service';
import PropertyService from '../../../services/property.service';
import PlatformService from '../../../services/platform.service';
import UserService from '../../../services/user.service';
import CommonService from '../../../services/common.service';
import { SortOrder, Modules } from '../../../enums';
import { usePersistentFilters } from '../../../hooks/usePersistentFilters';

const List: React.FC = () => {
  const { t } = useTranslation();

  const defaultFilters: ListFilters = {
    searchText: '',
    property: '',
    platform: '',
    reservationStatus: '',
    status: '',
    source: '',
    assignedTo: '',
    reviewedBy: '',
    isActive: '',
    ...getStartEndOfMonth(),
  };

  const { initialFilters, saveFilters, resetFilters } = usePersistentFilters<ListFilters>('filters_reservations', defaultFilters);

  const [filters, setFilters] = useState<ListFilters>(initialFilters);

  const [dropdownSearchData, setDropdownSearchData] = useState<any>({
    property: '',
    status: '',
    platform: '',
    reservationStatus: '',
    source: '',
    assignedTo: '',
    reviewedBy: '',
    isActive: ''
  });
  const [kpis, setKpis] = useState<any>({});
  const [counter, setCounter] = useState<number>(0);
  const headCells: HeadCellType[] = buildHeadCells([
    VALIDATION_FIELD_MAPPING.property,
    VALIDATION_FIELD_MAPPING.checkInDetails,
    VALIDATION_FIELD_MAPPING.guest,
    VALIDATION_FIELD_MAPPING.reservationStatus,
    VALIDATION_FIELD_MAPPING.hostPayout,
    VALIDATION_FIELD_MAPPING.status,
    VALIDATION_FIELD_MAPPING.invoiceNumber,
    VALIDATION_FIELD_MAPPING.source,
    VALIDATION_FIELD_MAPPING.actions
  ], 'reservationList');

  const location = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const {
    order, orderBy, setOrder, setOrderBy
  } = useSorting({ defaultOrder: (searchParams.get('order') as SortOrder) || SortOrder.asc, defaultOrderBy: searchParams.get('orderBy') || VALIDATION_FIELD_MAPPING.checkInDetails });

  const {
    page, perPage, setPage, setPerPage
  } = usePagination({ defaultPage: Number(searchParams.get('page')) || 1 });

  useUrlParams({
    type: Modules.reservations, uri: Modules.reservations, perPage, page, order, orderBy,
  });

  const { total, data: reservations, loading } = useFetch({
    service: ReservationService,
    func: 'list',
    payload: {
      page,
      perPage,
      orderBy: (HEAD_CELL_SORT_MAPPING.reservationList as any)[orderBy] || orderBy,
      order,
      ...filters
    },
    dependency: [order, orderBy, page, perPage, filters.searchText, filters.property, filters.platform, filters.reservationStatus, filters.status, filters.source, filters.assignedTo, filters.reviewedBy, filters.startDate, filters.endDate, filters.isActive, counter],
    onSuccess: (response: any) => {
      setKpis({
        unbilledCount: response.total_unbilled_count ?? 0,
        invoicedCount: response.total_invoiced_count ?? 0,
        settledCount: response.total_settled_count ?? 0
      });
    },
    onError: () => {
      setKpis({
        unbilledCount: 0,
        invoicedCount: 0,
        settledCount: 0
      });
    }
  });

  const { data: allStatus } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.transactionStatus, searchText: '' },
    dependency: []
  });

  const { data: status } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.transactionStatus, searchText: dropdownSearchData.status },
    dependency: [dropdownSearchData.status]
  });

  const { data: allProperties } = useFetch({
    service: PropertyService,
    func: 'lookup',
    payload: { searchText: '', is_active: '1' },
    dependency: [],
  });

  const { data: properties } = useFetch({
    service: PropertyService,
    func: 'lookup',
    payload: { searchText: dropdownSearchData.property, is_active: '1' },
    dependency: [dropdownSearchData.property],
  });

  const { data: allPlatforms } = useFetch({
    service: PlatformService,
    func: 'lookup',
    payload: { searchText: '', is_active: '1' },
    dependency: [],
  });

  const { data: platforms } = useFetch({
    service: PlatformService,
    func: 'lookup',
    payload: { searchText: dropdownSearchData.platform, is_active: '1' },
    dependency: [dropdownSearchData.platform],
  });

  const { data: allReservationStatus } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.reservationStatus, searchText: '' },
    dependency: []
  });

  const { data: reservationStatus } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.reservationStatus, searchText: dropdownSearchData.reservationStatus },
    dependency: [dropdownSearchData.reservationStatus]
  });

  const { data: allSources } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.source, searchText: '' },
    dependency: []
  });

  const { data: sources } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.source, searchText: dropdownSearchData.source },
    dependency: [dropdownSearchData.source]
  });

  const { data: allReviewedByOptions } = useFetch({
    service: UserService,
    func: 'lookup',
    payload: { searchText: '', is_active: '1' },
    dependency: [],
  });

  const { data: reviewedByOptions } = useFetch({
    service: UserService,
    func: 'lookup',
    payload: { searchText: dropdownSearchData.reviewedBy, is_active: '1' },
    dependency: [dropdownSearchData.reviewedBy],
  });

  const { data: allManagers } = useFetch({
    service: UserService,
    func: 'managersLookup',
    payload: { searchText: '', is_active: '1' },
    dependency: []
  });

  const { data: managers } = useFetch({
    service: UserService,
    func: 'managersLookup',
    payload: { searchText: dropdownSearchData.assignedTo, is_active: '1' },
    dependency: [dropdownSearchData.assignedTo]
  });

  const { data: allActiveStatus } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.status, searchText: '' },
    dependency: []
  });

  const { data: activeStatus } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.status, searchText: dropdownSearchData.isActive },
    dependency: [dropdownSearchData.isActive]
  });

  const handleApplyFilters = useCallback((newFilters: ListFilters) => {
    setPage(1);
    setFilters(newFilters);
    saveFilters(newFilters);
  }, [saveFilters, setPage]);

  const handleResetFilters = useCallback(() => {
    setPage(1);
    setFilters(defaultFilters);
    resetFilters();
  }, [resetFilters, setPage]);

  const handleDropdownSearchInputChange = useCallback((event: any, name: string) => {
    setDropdownSearchData((prev: any) => {
      return {
        ...prev,
        [name]: event.target.value,
      };
    });
  }, [setDropdownSearchData]);

  const handleRefreshClick = useCallback(() => {
    setCounter((prev) => prev + 1);
  }, []);

  const headerSection = <TableHeaderSection total={total} kpis={kpis} />

  const values: TableContextProvider = useMemo(() => {
    return {
      headerSection, row: Row, data: reservations || [], pagination: true, headCells, order, orderBy, page, perPage, total, loading, noDataFoundMessage: t('reservations.list.pageTexts.table.noDataFoundMessage'), setPage, setPerPage, setOrder, setOrderBy,
    };
  }, [headCells, order, orderBy, page, perPage, total, loading, reservations, setPage, setPerPage, setOrder, setOrderBy]);

  return (
    <>
      <Header onRefresh={handleRefreshClick} />

      <Filters
        status={status}
        allStatus={allStatus}
        platforms={platforms}
        allPlatforms={allPlatforms}
        properties={properties}
        allProperties={allProperties}
        allReservationStatus={allReservationStatus}
        reservationStatus={reservationStatus}
        allSourecs={allSources}
        sources={sources}
        allManagers={allManagers}
        managers={managers}
        reviewedByOptions={reviewedByOptions}
        allReviewedByOptions={allReviewedByOptions}
        dropdownSearchData={dropdownSearchData}
        onDropdownSearchChange={handleDropdownSearchInputChange}
        initialFilters={initialFilters}
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
        activeStatus={activeStatus}
        allActiveStatus={allActiveStatus}
      />

      <TableBox tableValues={values} />
    </>
  );
};

export default List;
